import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAraAuth } from '../contexts/AraAuthContext';
import { useAraTheme } from '../contexts/AraThemeContext';
import { LockKey, Eye, EyeSlash, ArrowLeft } from 'phosphor-react';
import { toast } from 'sonner';
import { getAraAppState } from '../utils/ara-storage';

const AraUnlock = () => {
  const [passcode, setPasscode] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const { unlock } = useAraAuth();
  const { theme } = useAraTheme();
  const navigate = useNavigate();
  const logo = theme === 'dark' ? '/ara-logo-dark.png' : '/ara-logo-light.png';

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await unlock(passcode);
      // Route to onboarding only if the supporter has never completed it.
      const done = await getAraAppState('ara_onboarding_completed');
      navigate(done ? '/ara/chat' : '/ara/onboarding');
    } catch (err) {
      toast.error('Invalid passcode');
      setPasscode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ara-root min-h-screen flex flex-col" data-ara-theme={theme} data-testid="ara-unlock">
      <header className="max-w-3xl mx-auto w-full px-6 py-4">
        <button
          onClick={() => navigate('/ara')}
          className="inline-flex items-center gap-2 ara-text-muted hover:ara-text transition-colors text-sm"
          data-testid="ara-unlock-back"
        >
          <ArrowLeft size={16} /> Back
        </button>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-6">
            <img src={logo} alt="Ara" className="w-20 h-20 rounded-full mb-4 ara-logo-glow" />
            <div className="ara-chip rounded-full p-3 mb-3"><LockKey size={22} weight="duotone" /></div>
            <h1 className="text-3xl font-serif font-semibold ara-text" data-testid="ara-unlock-title">Welcome back</h1>
            <p className="text-sm ara-text-muted text-center mt-2">
              Enter your Ara passcode to open your sanctuary.
            </p>
          </div>
          <form onSubmit={submit} className="ara-card rounded-3xl p-6 space-y-5">
            <div className="relative">
              <input
                type={show ? 'text' : 'password'}
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                className="ara-input w-full rounded-xl px-4 py-3"
                placeholder="Enter passcode"
                data-testid="ara-unlock-passcode-input"
                autoFocus
                required
              />
              <button
                type="button"
                onClick={() => setShow(!show)}
                className="absolute right-3 top-1/2 -translate-y-1/2 ara-text-muted"
              >
                {show ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <button
              type="submit"
              disabled={busy}
              className="ara-cta-primary w-full rounded-full px-6 py-3 font-medium disabled:opacity-50"
              data-testid="ara-unlock-btn"
            >
              {busy ? 'Unlocking…' : 'Unlock'}
            </button>
            <p className="text-xs ara-text-muted text-center">
              Forgot your passcode? Your Ara data cannot be recovered.
            </p>
          </form>
        </div>
      </main>
    </div>
  );
};

export default AraUnlock;
